/**
 * Charts, drawn by hand in the console's own language: hairline rules, one gold accent,
 * mono for every number, sharp corners. No chart library — an SVG the same weight as the
 * rest of the chrome reads as part of the instrument, not a widget bolted onto it. All
 * colour comes from CSS variables, so every chart follows the theme without a redraw.
 */
import type { ReactNode } from "react";

export interface Slice {
  label: string;
  value: number;
  color: string;
}

/**
 * A ring. The total sits in the middle in mono; each slice is a stroked arc. Empty input
 * draws a single hairline ring rather than nothing, so an empty corpus still reads as a
 * chart at rest instead of a broken one.
 */
export function Donut({
  slices,
  total,
  unit,
  size = 132,
}: {
  slices: Slice[];
  total: number;
  unit: string;
  size?: number;
}) {
  const stroke = 12;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const sum = slices.reduce((a, s) => a + s.value, 0) || 1;
  let offset = 0;

  return (
    <div className="flex items-center gap-4">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="shrink-0">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="var(--hair-strong)"
          strokeWidth={stroke}
          opacity={0.5}
        />
        {slices.map((s) => {
          const len = (s.value / sum) * c;
          const seg = (
            <circle
              key={s.label}
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="none"
              stroke={s.color}
              strokeWidth={stroke}
              strokeDasharray={`${len} ${c - len}`}
              strokeDashoffset={-offset}
              transform={`rotate(-90 ${size / 2} ${size / 2})`}
            />
          );
          offset += len;
          return seg;
        })}
        <text
          x="50%"
          y="47%"
          textAnchor="middle"
          className="tnum fill-[var(--text)] font-mono font-bold"
          style={{ fontSize: 26 }}
        >
          {total}
        </text>
        <text
          x="50%"
          y="62%"
          textAnchor="middle"
          className="fill-[var(--text-faint)] font-mono uppercase"
          style={{ fontSize: 8.5, letterSpacing: "0.2em" }}
        >
          {unit}
        </text>
      </svg>
      <ul className="flex min-w-0 flex-col gap-1.5">
        {slices.map((s) => (
          <li key={s.label} className="flex items-center gap-2 font-mono text-[10.5px]">
            <i className="size-2.5 shrink-0" style={{ background: s.color }} />
            <span className="truncate tracking-[0.12em] text-dim uppercase">{s.label}</span>
            <b className="tnum ms-auto ps-2 text-text">{s.value}</b>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Horizontal bars, longest-first is the caller's job. Each row is a label, a hairline
 * track with a gold fill, and the count — the whole thing scaled to `max`.
 */
export function BarList({
  items,
  color = "var(--gold-surface)",
  empty = "no data",
}: {
  items: { label: ReactNode; value: number; key: string }[];
  color?: string;
  empty?: string;
}) {
  const max = Math.max(1, ...items.map((i) => i.value));
  if (items.length === 0) {
    return <p className="px-1 py-4 font-mono text-[10.5px] tracking-[0.16em] text-faint uppercase">{empty}</p>;
  }
  return (
    <ul className="flex flex-col gap-2">
      {items.map((i) => (
        <li key={i.key} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1">
          <span className="truncate font-mono text-[11px] tracking-[0.06em] text-dim">{i.label}</span>
          <b className="tnum font-mono text-[12px] font-bold text-text">{i.value}</b>
          <span className="col-span-2 h-[5px] bg-hair-soft">
            <i
              className="block h-full"
              style={{ width: `${(i.value / max) * 100}%`, background: color }}
            />
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * A sparkline of counts over time as thin bars — for "how much, lately". Baseline hairline
 * keeps it grounded even when every bucket is zero.
 */
export function Spark({
  values,
  height = 38,
  color = "var(--gold-surface)",
}: {
  values: number[];
  height?: number;
  color?: string;
}) {
  const max = Math.max(1, ...values);
  const n = Math.max(values.length, 1);
  const gap = 2;
  return (
    <svg width="100%" height={height} viewBox={`0 0 ${n * 6} ${height}`} preserveAspectRatio="none">
      <line x1="0" y1={height - 0.5} x2={n * 6} y2={height - 0.5} stroke="var(--hair-strong)" strokeWidth="1" />
      {values.map((v, i) => {
        const h = (v / max) * (height - 4);
        return (
          <rect
            key={i}
            x={i * 6 + gap / 2}
            y={height - h}
            width={6 - gap}
            height={h}
            fill={v > 0 ? color : "var(--hair-strong)"}
          />
        );
      })}
    </svg>
  );
}
